import { Button, Card, Field, FormGrid, Input, NumberInput, PageHeader, Skeleton, Textarea, toast } from "@cafe/ui";
import { SOCIAL_NETWORKS, siteContactsSchema, type SiteContacts, type SocialNetwork } from "@cafe/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, errorText, http, useCan } from "../lib/api";

type S = { trialDays: number; graceDays: number; readOnlyDays: number; invoiceLeadDays: number; invoiceDueDays: number; bankDetails: string | null; contacts: SiteContacts | null };

const SOCIAL_LABEL: Record<SocialNetwork, string> = { instagram: "Instagram", whatsapp: "WhatsApp", telegram: "Telegram", tiktok: "TikTok", facebook: "Facebook", youtube: "YouTube" };
const SOCIAL_PLACEHOLDER: Record<SocialNetwork, string> = { instagram: "@coca.kg", whatsapp: "+996 555 123 456", telegram: "@coca_kg", tiktok: "@coca.kg", facebook: "facebook.com/coca.kg", youtube: "@coca" };

/** Глобальные параметры биллинга (ТЗ 03 §5): trial, grace, read-only, счета, реквизиты для перевода; контакты и соцсети Coca для лендинга. */
export default function SettingsPage() {
  const can = useCan();
  const manage = can("settings.manage");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings"], queryFn: () => http.get<S>("/settings") });
  const [f, setF] = useState<S | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => { if (q.data) setF(q.data); }, [q.data]);
  if (!f) return <Skeleton className="h-64" />;
  const num = (k: "trialDays" | "graceDays" | "readOnlyDays" | "invoiceLeadDays" | "invoiceDueDays", label: string, hint?: string) => <Field label={label} hint={hint}><NumberInput stepper value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v ?? 0 })} min={0} max={90} disabled={!manage} /></Field>;
  const contact = (k: keyof SiteContacts, v: string) => {
    setF({ ...f, contacts: { ...f.contacts, [k]: v } });
    if (errors[k]) setErrors(({ [k]: _, ...rest }) => rest);
  };

  const save = () => {
    const c = siteContactsSchema.safeParse(f.contacts ?? {});
    if (!c.success) {
      setErrors(Object.fromEntries(c.error.issues.map((i) => [String(i.path[0]), i.message])));
      toast.error("Проверьте контакты");
      return;
    }
    setErrors({});
    void http.put("/settings", { trialDays: f.trialDays, graceDays: f.graceDays, readOnlyDays: f.readOnlyDays, invoiceLeadDays: f.invoiceLeadDays, invoiceDueDays: f.invoiceDueDays, bankDetails: f.bankDetails || null, contacts: c.data })
      .then(() => { toast.success("Сохранено"); void qc.invalidateQueries({ queryKey: ["settings"] }); }, (e) => {
        if (e instanceof ApiError) setErrors(Object.fromEntries(e.fields.filter((x) => x.path.startsWith("contacts.")).map((x) => [x.path.slice(9), x.message])));
        toast.error(errorText(e));
      });
  };

  return (
    <>
      <PageHeader title="Настройки платформы" description="Сроки пробного периода, оплаты и что происходит, если клиент не заплатил. Контакты Coca на сайте." />
      <Card className="flex flex-col gap-5 p-5">
        <FormGrid cols={3}>
          {num("trialDays", "Бесплатный пробный период, дней", "Для новых клиентов")}
          {num("graceDays", "Отсрочка оплаты, дней", "Клиент не заплатил вовремя, но работает как обычно. Потом — только просмотр")}
          {num("readOnlyDays", "Только просмотр, дней", "Клиент видит данные и продаёт на кассе, но не может ничего менять. Потом доступ закрывается")}
          {num("invoiceLeadDays", "Выставлять счёт за … дней до конца оплаты")}
          {num("invoiceDueDays", "Сколько дней на оплату счёта")}
        </FormGrid>
        <Field label="Банковские реквизиты для оплаты" hint="Клиент увидит их в счёте"><Textarea value={f.bankDetails ?? ""} onChange={(e) => setF({ ...f, bankDetails: e.target.value })} rows={4} disabled={!manage} /></Field>
        <div className="flex flex-col gap-4 rounded-tile border border-border p-4">
          <div>
            <p className="font-semibold">Контакты и соцсети Coca</p>
            <p className="text-sm text-muted">Видны на сайте Coca (внизу каждой страницы) и поисковикам. Что не заполнено — не показывается. Соцсети — ссылкой или @именем, WhatsApp — номером.</p>
          </div>
          <FormGrid cols={2}>
            <Field label="Телефон" error={errors.phone}><Input type="tel" value={f.contacts?.phone ?? ""} onChange={(e) => contact("phone", e.target.value)} placeholder="+996 555 123 456" disabled={!manage} /></Field>
            <Field label="Email" error={errors.email}><Input type="email" value={f.contacts?.email ?? ""} onChange={(e) => contact("email", e.target.value)} placeholder="hello@coca.kg" disabled={!manage} /></Field>
            {SOCIAL_NETWORKS.map((n) => (
              <Field key={n} label={SOCIAL_LABEL[n]} error={errors[n]}>
                <Input value={f.contacts?.[n] ?? ""} onChange={(e) => contact(n, e.target.value)} placeholder={SOCIAL_PLACEHOLDER[n]} maxLength={300} disabled={!manage} />
              </Field>
            ))}
          </FormGrid>
        </div>
        {manage && <Button className="self-start" onClick={save}>Сохранить</Button>}
      </Card>
    </>
  );
}
